/**
 * Statuts de paiement d'un contrat (colonne `MONTHLY_PAYMENT_STATUS`, alimentée
 * par les webhooks/crons Stripe). Partagé entre la carte paiement client
 * (`PaymentDetailsCard`) et l'onglet « Contrats » du dashboard Stripe, pour éviter
 * la divergence des libellés/couleurs et couvrir tout le périmètre canonique.
 *
 * Périmètre canonique (cf. `api/src/contracts/monthly-payment.constants.ts` et les
 * assignations webhook) : initialized, ongoing, succeeded, failed,
 * retry_in_progress, payment_failed_twice, payment_method_updated, canceled_undone,
 * canceled_unpaid, canceled_manual, canceled_dispute_lost.
 */
export const PAYMENT_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  initialized: { label: 'Initialisé', color: 'tw-bg-gray-100 tw-text-gray-700' },
  ongoing: { label: 'En cours', color: 'tw-bg-blue-100 tw-text-blue-700' },
  succeeded: { label: 'Terminé', color: 'tw-bg-green-100 tw-text-green-700' },
  failed: { label: 'Échoué', color: 'tw-bg-red-100 tw-text-red-700' },
  retry_in_progress: { label: 'Relance en cours', color: 'tw-bg-orange-100 tw-text-orange-700' },
  payment_failed_twice: { label: 'Échec x2', color: 'tw-bg-red-100 tw-text-red-700' },
  canceled_undone: { label: 'Annulé', color: 'tw-bg-gray-100 tw-text-gray-700' },
  canceled_unpaid: { label: 'Impayés', color: 'tw-bg-red-100 tw-text-red-700' },
  canceled_manual: { label: 'Annulé manuellement', color: 'tw-bg-gray-100 tw-text-gray-700' },
  canceled_dispute_lost: { label: 'Opposition au paiement', color: 'tw-bg-red-100 tw-text-red-700' },
  payment_method_updated: { label: 'Nouveau moyen de paiement', color: 'tw-bg-blue-100 tw-text-blue-700' },
  unknown: { label: 'Inconnu', color: 'tw-bg-gray-100 tw-text-gray-700' },
};

/** Statuts « en attente » (pastille non terminale) — usage carte paiement. */
export const PENDING_STATUSES = ['initialized', 'ongoing', 'failed', 'retry_in_progress'];

/**
 * Statuts pour lesquels le mandat SEPA client est téléchargeable (contrat
 * mensualisé uniquement — préfixe `sub_`/`seti_`). Aligné avec le gating serveur
 * de `getClientSepaMandatePdf`.
 */
export const MANDATE_AVAILABLE_STATUSES = new Set([
  'ongoing',
  'succeeded',
  'failed',
  'retry_in_progress',
  'payment_failed_twice',
  'canceled_unpaid',
  'canceled_manual',
  'payment_method_updated',
]);

/** Libellé + couleur d'un statut, avec repli sur « Inconnu » pour tout statut hors périmètre. */
export const getPaymentStatusInfo = (status?: string | null) =>
  (status && PAYMENT_STATUS_LABELS[status]) || PAYMENT_STATUS_LABELS.unknown;
